// src/app/modules/Technical/components/Machinery/MachineryUploadPage.tsx
import React, { FC, useState, useEffect } from 'react'
import { useAuth } from '../../../auth'
import { getVesselList } from '../../../Management/core/_requests'
import { uploadMachinery, downloadMachineryTemplate, BulkUploadResult, BulkUploadError } from '../../core/_requests'
import type { Vessel } from '../../../Management/core/_models'
import { toast, ToastContainer } from 'react-toastify'
import { KTSVG } from '../../../../../_metronic/helpers'
import { getAccessibleVessels, isVesselAssignedUserOnly } from '../../core/technicalScopeUtils'
import { TECH_PERM } from '../../core/technicalPermissions'
import { TechnicalAccessDenied } from '../TechnicalAccessDenied'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

export const MachineryUploadPage: FC = () => {
  const { currentUser, isSuperAdmin, hasPermission } = useAuth()
  const isVesselOnlyUser = isVesselAssignedUserOnly(currentUser, isSuperAdmin)
  const canImportMachinery = hasPermission(TECH_PERM.IMPORT_MACHINERY)

  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedVesselId, setSelectedVesselId] = useState<number | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null)

  useEffect(() => {
    const loadVessels = async () => {
      try {
        const vesselList = await getVesselList()
        const vesselsForUser = getAccessibleVessels(vesselList, currentUser, isSuperAdmin)

        setVessels(vesselsForUser)
        if (isVesselOnlyUser && currentUser?.vessel?.id) {
          setSelectedVesselId(currentUser.vessel.id)
        } else if (!isVesselOnlyUser && vesselsForUser.length > 0 && !selectedVesselId) {
          setSelectedVesselId(vesselsForUser[0].id)
        }
      } catch (error) {
        console.error('Error loading vessels:', error)
        toast.error('Failed to load vessels')
      }
    }

    loadVessels()
  }, [currentUser, isSuperAdmin, isVesselOnlyUser])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const validExtensions = ['.xlsx', '.xls', '.csv']
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
      if (!validExtensions.includes(extension)) {
        toast.error('Please select an Excel (.xlsx, .xls) or CSV (.csv) file')
        return
      }
      setSelectedFile(file)
      setUploadResult(null)
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadMachineryTemplate()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'machinery_upload_template.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Template downloaded successfully')
    } catch (error: any) {
      console.error('Error downloading template:', error)
      toast.error('Failed to download template')
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file')
      return
    }
    if (!selectedVesselId) {
      toast.error('Please select a vessel')
      return
    }

    setUploading(true)
    try {
      const result = await uploadMachinery(selectedFile, selectedVesselId)
      setUploadResult(result)
      if (result.errorCount === 0) {
        toast.success(`Successfully uploaded ${result.successCount} rows`)
      } else {
        toast.warning(`Upload completed with ${result.errorCount} errors`)
      }
    } catch (error: any) {
      console.error('Error uploading file:', error)
      toast.error('Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  if (!canImportMachinery) {
    return (
      <TechnicalAccessDenied
        title='Machinery Bulk Upload'
        message='You do not have permission to import machinery data.'
      />
    )
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <h3 className='fw-bold m-0'>Machinery Bulk Upload</h3>
        </div>
      </div>
      <div className='card-body'>
        <div className='row g-5'>
          {/* Vessel Selection */}
          <div className='col-md-12'>
            <label className={LABEL}>
              Vessel <span className='text-danger'>*</span>
            </label>
            {isVesselOnlyUser ? (
              <input
                type='text'
                className='form-control'
                value={currentUser?.vessel?.fleet_name || 'Vessel'}
                disabled
              />
            ) : (
              <SearchableSelect
                className='form-select'
                value={selectedVesselId || ''}
                onChange={(e) => setSelectedVesselId(Number(e.target.value))}
              >
                <option value=''>Select Vessel</option>
                {vessels.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.fleet_name}
                  </option>
                ))}
              </SearchableSelect>
            )}
          </div>

          {/* File Selection */}
          <div className='col-md-12'>
            <label className={LABEL}>
              Upload File <span className='text-danger'>*</span>
            </label>
            <input
              type='file'
              className='form-control'
              accept='.xlsx,.xls,.csv'
              onChange={handleFileSelect}
            />
            <div className='form-text'>
              Supported formats: Excel (.xlsx, .xls) or CSV (.csv)
            </div>
          </div>

          {/* Template Download */}
          <div className='col-md-12'>
            <button
              type='button'
              className='btn btn-light-primary btn-sm'
              onClick={handleDownloadTemplate}
            >
              <KTSVG path='/media/icons/duotune/files/fil021.svg' className='svg-icon-3' />
              Download Template
            </button>
              <div className='form-text mt-2'>
                Template includes 4 tabs: Vessel Locations, Jobs Library, Vessel Multiplicities, and Spares.
                Use Equipment Code to link Vessel Multiplicities and Spares, and SubComponent Code to link Jobs Library and Spares.
                Use the optional Instance Of column in Vessel Locations to group similar systems.
              </div>
            </div>

          {/* Upload Button */}
          <div className='col-md-12'>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleUpload}
              disabled={!selectedFile || !selectedVesselId || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>

          {/* Upload Results */}
          {uploadResult && (
            <div className='col-md-12'>
              <div className='card bg-light'>
                <div className='card-body'>
                  <h5 className='card-title'>Upload Results</h5>
                  <div className='row mb-3'>
                    <div className='col-md-4'>
                      <div className='text-center'>
                        <div className='fs-3 fw-bold text-primary'>{uploadResult.totalRows}</div>
                        <div className='text-muted'>Total Rows</div>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-center'>
                        <div className='fs-3 fw-bold text-success'>{uploadResult.successCount}</div>
                        <div className='text-muted'>Success</div>
                      </div>
                    </div>
                    <div className='col-md-4'>
                      <div className='text-center'>
                        <div className='fs-3 fw-bold text-danger'>{uploadResult.errorCount}</div>
                        <div className='text-muted'>Errors</div>
                      </div>
                    </div>
                  </div>

                  {uploadResult.errors.filter((error) => !(error.message || '').toLowerCase().startsWith('warning:')).length > 0 && (
                    <div className='mt-3'>
                      <h6 className='fw-bold'>Errors:</h6>
                      <div className='table-responsive'>
                        <table className='table table-sm table-bordered'>
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>Message</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.errors
                              .filter((error) => !(error.message || '').toLowerCase().startsWith('warning:'))
                              .map((error, idx) => (
                                <tr key={idx}>
                                  <td>{error.rowNumber}</td>
                                  <td className='text-danger'>{error.message}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: "5rem" }}
      />
    </div>
  )
}

